import alt from 'components/Dispatcher';
import generateAsyncActions from 'utils/generateAsyncActions';
import {ActionsBase, DispatchAll, IncludeResultCallbacks} from '@store/@types/alt';
import {List} from 'immutable';
import {KpiObservation, KpiObservationId, KpiRequest, CoachingKpi} from '@store/@types/kpi';
import KpiObservationSource, {
  CreateObservationRequest,
  ObservationBySkillsRequest,
  ObservationSearchRequest,
  UpdateObservationRequest,
} from '@store/kpi/KpiObservationSource';
import {Int} from '@store/@types/util';
import {UserId} from 'records/User';

interface AsyncActions {
  search(params: ObservationSearchRequest): Promise<{
    userId: UserId;
    total: Int;
    kpiObservations: List<KpiObservation>;
  }>;
  bySkills(params: ObservationBySkillsRequest): Promise<List<KpiObservation>>;
  coachingKpis(params: ObservationBySkillsRequest): Promise<[List<CoachingKpi>, UserId]>;
  create(params: CreateObservationRequest): Promise<void>;
  update(id: KpiObservationId, params: UpdateObservationRequest): Promise<void>;
  getReminders(userId: UserId): Promise<[List<KpiRequest>, UserId]>;
}

interface SyncActions {}

export interface KpiObservationActionsInterface
  extends IncludeResultCallbacks<AsyncActions>,
    SyncActions {}

class KpiObservationActions
  extends ActionsBase<AsyncActions, SyncActions>()
  implements DispatchAll<AsyncActions>
{
  constructor() {
    super();
    this.generateActions(
      ...generateAsyncActions<AsyncActions>(
        'search',
        'bySkills',
        'create',
        'update',
        'getReminders',
        'coachingKpis'
      )
    );
  }

  search(params: ObservationSearchRequest) {
    return dispatch => {
      dispatch(params);
      return KpiObservationSource.search(params).then(this.searchSuccess, this.searchError);
    };
  }

  bySkills(params: ObservationBySkillsRequest) {
    return dispatch => {
      dispatch(params);
      return KpiObservationSource.bySkills(params).then(this.bySkillsSuccess, this.bySkillsError);
    };
  }

  create(params: CreateObservationRequest) {
    return dispatch => {
      dispatch(params);
      return KpiObservationSource.create(params).then(this.createSuccess, this.createError);
    };
  }

  update(id: KpiObservationId, params: UpdateObservationRequest) {
    return dispatch => {
      dispatch(id, params);
      return KpiObservationSource.update(id, params).then(this.updateSuccess, this.updateError);
    };
  }

  getReminders(userId: UserId) {
    return dispatch => {
      dispatch(userId);
      return KpiObservationSource.getReminders(userId).then(
        r => this.getRemindersSuccess(r, userId),
        this.getRemindersError
      );
    };
  }

  coachingKpis(params: ObservationBySkillsRequest) {
    return dispatch => {
      dispatch(params);
      return KpiObservationSource.coachingKpis(params).then(
        r => this.coachingKpisSuccess(r, params.userId),
        this.coachingKpisError
      );
    };
  }
}

export default alt.createActions<AsyncActions, SyncActions>(KpiObservationActions);
